import React, { useState, useCallback, useEffect } from 'react';
import { Slate, Editable, withReact } from 'slate-react';
import { createEditor, Transforms, Editor, Text } from 'slate';
import { withHistory } from 'slate-history';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Save,
  Download,
  Loader,
  FileText
} from 'lucide-react';
import useAppStore from '../store/appStore';
import toast from 'react-hot-toast';

const ConsolidatedEditor = ({ draft, isLoading }) => {
  const [editor] = useState(() => withHistory(withReact(createEditor())));
  const [value, setValue] = useState([
    {
      type: 'paragraph',
      children: [{ text: draft || 'Loading consolidated regulation...' }],
    },
  ]);

  const { selectedRegulations, regulations } = useAppStore();

  useEffect(() => {
    if (draft) {
      setValue([
        {
          type: 'paragraph',
          children: [{ text: draft }],
        },
      ]);
    }
  }, [draft]);

  const handleSave = () => {
    const content = value.map(n => Node.string(n)).join('\n');
    // Save to backend or local storage
    toast.success('Draft saved successfully');
  };

  const handleExport = () => {
    const content = value.map(n => Node.string(n)).join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'consolidated_regulation.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported successfully');
  };

  const toggleMark = (format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  const renderElement = useCallback(props => {
    switch (props.element.type) {
      case 'heading':
        return <h2 className="text-2xl font-playfair font-semibold mb-4" {...props.attributes}>{props.children}</h2>;
      case 'bulleted-list':
        return <ul className="list-disc list-inside mb-4" {...props.attributes}>{props.children}</ul>;
      case 'numbered-list':
        return <ol className="list-decimal list-inside mb-4" {...props.attributes}>{props.children}</ol>;
      case 'list-item':
        return <li {...props.attributes}>{props.children}</li>;
      default:
        return <p className="mb-4" {...props.attributes}>{props.children}</p>;
    }
  }, []);

  const renderLeaf = useCallback(props => {
    let { children } = props;
    if (props.leaf.bold) {
      children = <strong>{children}</strong>;
    }
    if (props.leaf.italic) {
      children = <em>{children}</em>;
    }
    if (props.leaf.underline) {
      children = <u>{children}</u>;
    }
    return <span {...props.attributes}>{children}</span>;
  }, []);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900">Consolidating Regulations...</p>
          <p className="text-sm text-gray-600 mt-2">
            Analyzing {selectedRegulations.length} regulations for consolidation
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onMouseDown={event => {
                event.preventDefault();
                toggleMark('bold');
              }}
              className={`p-2 rounded hover:bg-gray-100 ${isMarkActive(editor, 'bold') ? 'bg-gray-200' : ''}`}
            >
              <Bold className="h-4 w-4" />
            </button>
            <button
              onMouseDown={event => {
                event.preventDefault();
                toggleMark('italic');
              }}
              className={`p-2 rounded hover:bg-gray-100 ${isMarkActive(editor, 'italic') ? 'bg-gray-200' : ''}`}
            >
              <Italic className="h-4 w-4" />
            </button>
            <button
              onMouseDown={event => {
                event.preventDefault();
                toggleMark('underline');
              }}
              className={`p-2 rounded hover:bg-gray-100 ${isMarkActive(editor, 'underline') ? 'bg-gray-200' : ''}`}
            >
              <Underline className="h-4 w-4" />
            </button>
            
            <div className="w-px h-6 bg-gray-300 mx-2" />
            
            <button className="p-2 rounded hover:bg-gray-100">
              <List className="h-4 w-4" />
            </button>
            <button className="p-2 rounded hover:bg-gray-100">
              <ListOrdered className="h-4 w-4" />
            </button>
            
            <div className="w-px h-6 bg-gray-300 mx-2" />
            
            <button className="p-2 rounded hover:bg-gray-100">
              <AlignLeft className="h-4 w-4" />
            </button>
            <button className="p-2 rounded hover:bg-gray-100">
              <AlignCenter className="h-4 w-4" />
            </button>
            <button className="p-2 rounded hover:bg-gray-100">
              <AlignRight className="h-4 w-4" />
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSave}
              className="flex items-center space-x-2 px-3 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              <Save className="h-4 w-4" />
              <span className="text-sm">Save</span>
            </button>
            <button
              onClick={handleExport}
              className="flex items-center space-x-2 px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              <span className="text-sm">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto p-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-playfair font-semibold text-gray-900">
              Consolidated Regulation Draft
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Consolidated from {selectedRegulations.length} regulations • All provisions preserved • Redundancies removed
            </p>
          </div>

          <div className="prose max-w-none">
            <Slate
              editor={editor}
              value={value}
              onChange={newValue => setValue(newValue)}
            >
              <Editable
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder="Start typing or paste your consolidated regulation text..."
                className="min-h-[400px] focus:outline-none"
                spellCheck
                autoFocus
              />
            </Slate>
          </div>

          {/* Source Regulations */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Source Regulations</h3>
            <div className="space-y-2">
              {selectedRegulations.map(regId => {
                const reg = regulations.find(r => r.id === regId);
                if (!reg) return null;
                return (
                  <div key={regId} className="flex items-center space-x-2 text-sm">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="font-mono text-gray-600">{reg.number}</span>
                    <span className="text-gray-500">-</span>
                    <span className="text-gray-700">{reg.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedEditor;